import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import type { Branch, ContentNode, ContentTreeResponse, DocSearchResult, Section, MergePreviewResult, BranchDiffResult } from "../data/types";
import { authFetch } from "../utils/authFetch";
import { useProject } from "../contexts/ProjectContext";
import { useReadOnly } from "../contexts/ReadOnlyContext";
import { usePreview } from "../contexts/PreviewContext";
import { usePreviewApi } from "./usePreviewApi";
import { findFirstLeaf } from "../utils/treeNav";
import { useDocsBootstrap } from "./useDocsBootstrap";
import { useAuth } from "../contexts/AuthContext";

interface CmsContextValue {
  // Branch state
  branches: Branch[];
  activeBranch: Branch | null;
  setActiveBranch: (branch: Branch) => void;
  fetchBranches: () => Promise<void>;
  createBranch: (name: string, sourceBranchId: string, fromCommitId?: string, sourceOrigin?: "local" | "remote") => Promise<Branch>;
  deleteBranch: (id: string) => Promise<void>;
  forcePromoteBranch: (id: string) => Promise<Branch>;
  mergePreview: (id: string, targetId?: string) => Promise<MergePreviewResult>;
  branchDiff: (id: string, targetId?: string) => Promise<BranchDiffResult>;
  checkoutBranch: (branchId: string, branchName: string, branchSlug: string) => Promise<Branch>;

  // Sections
  sections: Section[];
  activeSection: Section | null;
  setActiveSection: (section: Section) => void;
  fetchSections: () => Promise<void>;
  createSection: (data: { title: string; slug: string; sectionType: string; baseUrl?: string }) => Promise<Section>;
  deleteSection: (sectionId: string) => Promise<void>;
  renameSection: (sectionId: string, title: string) => Promise<void>;
  updateSection: (sectionId: string, updates: { title?: string; baseUrl?: string; sectionType?: string; visibility?: string }) => Promise<void>;
  reorderSections: (orderedSlugs: string[], movedSlug?: string) => Promise<void>;

  // Tree state
  tree: ContentNode[];
  treeBranchId: string | null;
  stateHashes: Record<string, string>;
  loading: boolean;
  error: string | null;
  conflict: string | null;
  clearConflict: () => void;
  refetchTree: () => Promise<void>;

  // Node CRUD
  createNode: (data: Partial<ContentNode> & { previousHash?: string | null }) => Promise<ContentNode>;
  updateNode: (id: string, updates: Partial<ContentNode> & { previousHash?: string; baseContentHash?: string }) => Promise<ContentNode>;
  deleteNode: (id: string) => Promise<void>;
  reorderNodes: (parentId: string | null, order: string[], previousHash: string) => Promise<void>;
  moveNode: (nodeId: string, newParentId: string | null, position?: number) => Promise<ContentNode>;
  getNodeBySlug: (slugPath: string) => Promise<ContentNode>;
  searchDocs: (query: string) => Promise<DocSearchResult[]>;

  // Mutation counter — increments on any write op, used to trigger staging re-fetch
  mutationVersion: number;
  bumpMutation: () => void;

  // True while a branch switch is in progress — gates all downstream fetches
  // (staging, content, tree) to prevent intermediate state from leaking through.
  branchSwitching: boolean;

  // Suppress the next SSE branch-switch event (used by handleCheckoutLocal to avoid double reload)
  suppressBranchSwitchSSE: () => void;

  // Peek mode — time-travel through commit history (read-only)
  peekMode: boolean;
  peekCommitId: string | null;
  peekCommitMessage: string | null;
  peekCommitSequence: number | null;
  peekTree: ContentNode[];
  peekLoading: boolean;
  peekSections: Section[];
  setPeekMode: (on: boolean) => void;
  setPeekCommitId: (id: string | null, message?: string | null, sequence?: number | null) => void;

  // Global slug-path navigator — handles cross-section links
  navigateToPath: (targetPath: string) => void;

  // Bootstrap page data — when the docs bootstrap endpoint returns a page body,
  // it's stored here so ContentPageView can use it without a separate fetch.
  bootstrapPageData: React.MutableRefObject<Record<string, any> | null>;
}

const CmsContext = createContext<CmsContextValue | null>(null);

/** A deck in a non-presentations section: a category holding only cards. */
const isDeckNode = (n: any) => n?.nodeType === "category" && n.children?.length > 0 && n.children.every((c: any) => c.nodeType === "card");

/** Reader trees carry no bodies, and a card renders from its body. A deck can sit at any depth. */
async function withCardBodies(projectSlug: string | undefined, basePath: string, nodes: any[], isPresentations: boolean): Promise<any[]> {
  return Promise.all(nodes.map(async (node: any) => {
    if (!node.children?.length) return node;
    const path = `${basePath}/${node.slug}`;
    if (!isPresentations && !isDeckNode(node)) {
      return { ...node, children: await withCardBodies(projectSlug, path, node.children, false) };
    }
    const children = await Promise.all(node.children.map(async (card: any) => {
      if (card.body) return card;
      try {
        const r = await authFetch(`/api/docs/${projectSlug}/page/${path}/${card.slug}`);
        if (r.ok) {
          const page = await r.json();
          return { ...card, body: page.body || "", spec: page.spec || card.spec };
        }
      } catch { /* the card shows empty rather than breaking the page */ }
      return card;
    }));
    return { ...node, children };
  }));
}

const lacksCardBody = (nodes: any[]): boolean => nodes.some((n) => (n.nodeType === "card" && !n.body) || lacksCardBody(n.children ?? []));
const hasDeck = (nodes: any[]): boolean => nodes.some((n) => isDeckNode(n) || hasDeck(n.children ?? []));

export function CmsProvider({ children }: { children: ReactNode }) {
  const { projectSlug: urlProjectSlug, sectionSlug } = useParams<{ projectSlug: string; sectionSlug: string }>();
  const [searchParams] = useSearchParams();
  const readOnly = useReadOnly();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentProject, switchProject } = useProject();
  const { hydrateUser } = useAuth();

  // ── Preview context (edit mode) ──
  const {
    isLocalPreview,
    activeBranch: previewActiveBranch,
    activeSource: previewActiveSource,
    currentBranch: previewCurrentBranch,
    editMode,
    editBranch,
  } = usePreview();
  
  // Use project from URL - this is required for CMS routes
  const projectSlug = urlProjectSlug || currentProject?.slug;

  // ── Docs bootstrap (read-only mode) ──
  // Only fires in read-only (docs) mode. In studio mode, enabled=false skips the fetch.
  const { data: docsBootstrapData, loading: docsBootstrapLoading, error: docsBootstrapError } = useDocsBootstrap(readOnly);

  // Ref to hold the bootstrap page body so ContentPageView can read it
  // without making a separate /api/docs/{projectSlug}/page/{path} fetch.
  const bootstrapPageData = useRef<Record<string, any> | null>(null);

  // When URL has no ?branch= param (e.g. navigating back from settings),
  // fall back to the preview server's active branch instead of hardcoding "main".
  // In local preview mode, the preview server is the source of truth — ignore URL params.
  // In production mode, persist last branch in sessionStorage so it survives settings navigation.
  const urlBranch = searchParams.get("branch");
  const savedBranch = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("cortex_active_branch") : null;
  const branchSlug = readOnly ? "main" : (isLocalPreview ? (urlBranch || previewActiveBranch || "main") : (urlBranch || savedBranch || previewActiveBranch || "main"));
  const branchSource = searchParams.get("source");

  // Refs so fetchBranches callback identity doesn't change when branchSlug changes
  // (avoids the auto-effect re-firing and causing a second tree reload on branch switch)
  const branchSlugRef = useRef(branchSlug);
  branchSlugRef.current = branchSlug;
  const branchSourceRef = useRef(branchSource);
  branchSourceRef.current = branchSource;
  const activeSourceRef = useRef(previewActiveSource);
  activeSourceRef.current = previewActiveSource;

  // Ref for active section slug — used by fetchTree to avoid stale closure reads
  // when called immediately after fetchSections (React state hasn't flushed yet).
  const activeSectionSlugRef = useRef<string | undefined>(undefined);

  // Derive app base path from current URL. Supports `/studio`, `/docs`,
  // and `/share/<token>` (share-link visitors keep the share prefix on
  // every internal navigation so any URL stays copyable; shares are
  // always read-only docs surfaces, no `/docs` segment).
  const appBase = (() => {
    const p = location.pathname;
    const shareMatch = p.match(/^\/share\/[^/]+/);
    if (shareMatch) return shareMatch[0];
    return p.startsWith("/docs") ? "/docs" : "/studio";
  })();

  // Standalone routes (export preview + file history) reuse CmsProvider
  // for data but must NOT have the auto-navigation effects fire —
  // otherwise the user gets redirected back to the regular section view
  // as soon as the tree loads.
  const isPrintRoute =
    location.pathname.includes("/export/projects/") ||
    location.pathname.includes("/history/projects/");
  
  // Sync project context with URL
  useEffect(() => {
    if (urlProjectSlug && currentProject?.slug !== urlProjectSlug) {
      switchProject(urlProjectSlug);
    }
  }, [urlProjectSlug, currentProject?.slug, switchProject]);
  
  const [branches, setBranches] = useState<Branch[]>([]);
  const [activeBranch, setActiveBranchState] = useState<Branch | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [activeSection, setActiveSectionState] = useState<Section | null>(null);
  // Keep the ref in sync with state for use in fetchTree closures
  useEffect(() => { activeSectionSlugRef.current = activeSection?.slug; }, [activeSection?.slug]);
  const [tree, setTree] = useState<ContentNode[]>([]);
  const treeHasContent = useRef(false);
  // In read-only mode, store the full tree (all section roots) so we can
  // re-filter when the active section changes without another API call.
  const fullReadOnlyTree = useRef<ContentNode[]>([]);
  // Tracks which branch ID the current tree was fetched for.
  // ContentPageView uses this to avoid re-resolving slugs against a stale tree
  // during branch switches (prevents content flash).
  const [treeBranchId, setTreeBranchId] = useState<string | null>(null);
  const [stateHashes, setStateHashes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [mutationVersion, setMutationVersion] = useState(0);
  const bumpMutation = useCallback(() => setMutationVersion(v => v + 1), []);

  // Branch-switch transaction flag: when true, all downstream fetches (staging,
  // content resolution, tree) are gated so intermediate disk state doesn't leak.
  const [branchSwitching, setBranchSwitching] = useState(false);
  const branchSwitchingRef = useRef(false);

  const { objectStoreWrite } = usePreviewApi();

  // When handleCheckoutLocal already refreshed everything, suppress the SSE branch-switch
  // event that arrives shortly after to avoid a redundant second reload.
  const lastManualSwitchTime = useRef(0);
  const suppressBranchSwitchSSE = useCallback(() => {
    lastManualSwitchTime.current = Date.now();
  }, []);

  // ── Peek mode (time-travel) ──
  const [peekMode, setPeekModeState] = useState(false);
  const [peekCommitId, setPeekCommitIdState] = useState<string | null>(null);
  const [peekCommitMessage, setPeekCommitMessage] = useState<string | null>(null);
  const [peekCommitSequence, setPeekCommitSequence] = useState<number | null>(null);
  const [peekTree, setPeekTree] = useState<ContentNode[]>([]);
  const [peekSections, setPeekSections] = useState<Section[]>([]);
  const [peekSectionTrees, setPeekSectionTrees] = useState<Record<string, ContentNode[]>>({});
  const [peekLoading, setPeekLoading] = useState(false);

  const setPeekMode = useCallback((on: boolean) => {
    setPeekModeState(on);
    if (!on) {
      setPeekCommitIdState(null);
      setPeekCommitMessage(null);
      setPeekCommitSequence(null);
      setPeekTree([]);
      setPeekSections([]);
      setPeekSectionTrees({});
    }
  }, []);

  const setPeekCommitId = useCallback((id: string | null, message?: string | null, sequence?: number | null) => {
    setPeekCommitIdState(id);
    setPeekCommitMessage(message ?? null);
    setPeekCommitSequence(sequence ?? null);
  }, []);

  // Fetch snapshot tree when peekCommitId changes
  useEffect(() => {
    if (!peekMode || !peekCommitId || !activeBranch?.id) {
      if (!peekCommitId) { setPeekTree([]); setPeekSections([]); }
      return;
    }
    let cancelled = false;
    setPeekLoading(true);
    (async () => {
      try {
        const res = await authFetch(
          `/api/projects/${projectSlug}/studio/branches/${activeBranch.id}/snapshot/commit/${peekCommitId}?source=${getSourceParam()}`
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;

        // Sections come pre-built from the backend
        const snapshotSections: Section[] = (data.sections || []).map((s: any) => ({
          id: s.id || s.slug,
          projectId: "",
          slug: s.slug,
          title: s.title,
          sectionType: s.sectionType || "content",
          icon: null,
          homePage: null,
          createdAt: "",
          updatedAt: "",
        }));
        if (!cancelled) setPeekSections(snapshotSections);

        // Trees come pre-built per-section from the backend — use directly
        const sectionTreeMap: Record<string, ContentNode[]> = data.trees || {};
        if (!cancelled) {
          setPeekSectionTrees(sectionTreeMap);
          const activeSSlug = activeSection?.slug || sectionSlug || "";
          setPeekTree(sectionTreeMap[activeSSlug] || []);
        }
      } catch (err) {
        console.error("Failed to fetch peek snapshot:", err);
      } finally {
        if (!cancelled) setPeekLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [peekMode, peekCommitId, activeBranch?.id, projectSlug]);

  // Update peekTree when switching sections in peek mode
  useEffect(() => {
    if (!peekMode || Object.keys(peekSectionTrees).length === 0) return;
    const sSlug = activeSection?.slug || sectionSlug || "";
    setPeekTree(peekSectionTrees[sSlug] || []);
  }, [peekMode, activeSection?.slug, sectionSlug, peekSectionTrees]);

  // Track pending section switch for auto-navigation to first child
  const pendingSectionSwitch = useRef(false);

  // Helper: derive the `source` query param from the active branch's source field
  const getSourceParam = useCallback((branchSource?: string) => {
    const s = branchSource || activeBranch?.source;
    if (s === "remote") return "remote";
    return "local";
  }, [activeBranch?.source]);

  // findFirstLeaf imported from utils/treeNav

  const fetchBranches = useCallback(async () => {
    try {
      const res = await authFetch(`/api/projects/${projectSlug}/studio/branches`);
      if (!res.ok) throw new Error(`Failed to fetch branches: ${res.statusText}`);
      const data: Branch[] = await res.json();
      setBranches(data);
      const currentSlug = branchSlugRef.current;
      const currentSource = branchSourceRef.current;
      const serverSource = activeSourceRef.current;
      setActiveBranchState((current) => {
        if (current) {
          const updated = data.find((b) => b.id === current.id && b.source === current.source);
          if (updated) {
            const slugMatches = !currentSlug || updated.slug === currentSlug;
            if (slugMatches && (!currentSource || current.source === currentSource)) {
              return updated;
            }
          }
        }
        // In local preview mode, prefer the branch matching the server's activeSource
        const fromUrl = currentSlug
          ? (currentSource
              ? data.find((b) => b.slug === currentSlug && b.source === currentSource)
              : null)
            || (serverSource
              ? data.find((b) => b.slug === currentSlug && b.source === serverSource)
                || data.find((b) => b.name === currentSlug && b.source === serverSource)
              : null)
            || data.find((b) => b.slug === currentSlug)
          : null;
        const selected = fromUrl || data.find((b) => b.name === "main") || data[0] || null;
        return selected;
      });
    } catch (err) {
      setError(String(err));
    }
  }, [projectSlug]);

  const fetchTree = useCallback(async (branchId?: string, sourceOverride?: string) => {
    // In read-only mode, use published endpoint
    if (readOnly) {
      if (!treeHasContent.current) setLoading(true);
      try {
        const res = await authFetch(`/api/docs/${projectSlug}/tree`);
        if (!res.ok) throw new Error(`Failed to fetch docs tree: ${res.statusText}`);
        const data = await res.json();
        const allRoots = data.tree || data;
        fullReadOnlyTree.current = allRoots;
        // Filter to active section's children
        const sSlug = activeSection?.slug || sectionSlug;
        let sectionRoot = sSlug ? allRoots.find((n: any) => n.slug === sSlug) : null;
        if (!sectionRoot && allRoots.length > 0) sectionRoot = allRoots[0];
        const filtered = sectionRoot?.children || [];
        setTree(filtered);
        treeHasContent.current = filtered.length > 0;
        setError(null);
      } catch (err) {
        setError(String(err));
      } finally {
        setLoading(false);
      }
      return;
    }
    // When peeking at a non-active branch, use the preview currentBranch name
    const isPeeking = previewCurrentBranch && previewCurrentBranch !== previewActiveBranch;
    const bid = isPeeking ? previewCurrentBranch : (branchId || activeBranch?.id);
    if (!bid) return;
    // Only show loading spinner on initial load (empty tree).
    // During branch switch, keep the old tree visible to avoid a blank "Loading..." flash.
    if (!treeHasContent.current) setLoading(true);
    try {
      const sSlug = activeSectionSlugRef.current || activeSection?.slug || sectionSlug;
      const sectionParam = sSlug ? `&section_slug=${sSlug}` : "";
      const branchParam = bid;
      // Use sourceOverride when provided (e.g. during branch-switch SSE where
      // React state hasn't flushed yet and getSourceParam() would return stale value).
      const sourceParam = `&source=${sourceOverride || getSourceParam()}`;
      const url = `/api/projects/${projectSlug}/studio/nodes/tree?branch=${branchParam}${sectionParam}${sourceParam}`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`Failed to fetch tree: ${res.statusText}`);
      const data: ContentTreeResponse = await res.json();
      // Filter out virtual __root__ and unwrap section wrappers
      let filteredTree = data.tree.filter((n: ContentNode) => n.slug !== "__root__" && (n as any).nodeType !== "root");
      if (filteredTree.length > 0 && filteredTree.every((n: ContentNode) => n.nodeType === "section")) {
        filteredTree = filteredTree.flatMap((n: ContentNode) => n.children || []);
      }
      setTree(filteredTree);
      treeHasContent.current = filteredTree.length > 0;
      setTreeBranchId(bid);
      setStateHashes(data.stateHashes);
      setError(null);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [activeBranch?.id, activeSection?.slug, sectionSlug, projectSlug, getSourceParam, previewCurrentBranch, previewActiveBranch]);

  // Tick counter bumped by preview-reload events so the tree-fetch effect re-runs
  // even when the branch ID hasn't changed (e.g. after a commit on the same branch).
  const [reloadTick, setReloadTick] = useState(0);

  // ── Bootstrap: single request replaces branches → sections → tree waterfall ──
  // In local preview mode, skip bootstrap — the preview server doesn't intercept
  // the bootstrap endpoint, so it would return empty data from the backend.
  // Instead, fall back to individual fetches (branches, sections, tree) which
  // the preview server DOES intercept.
  const bootstrapDone = useRef(false);
  // Track the branch ID that bootstrap loaded — subsequent effects skip tree/sections
  // fetch only if they're for the same branch (prevents skipping when URL branch differs).
  const bootstrapBranchId = useRef<string | null>(null);
  useEffect(() => {
    if (!projectSlug) return;

    // In read-only mode (external /docs viewer), use published endpoints
    // instead of CMS endpoints. External users don't have CMS access.
    if (readOnly) {
      // Reset state for project switch
      fullReadOnlyTree.current = [];
      treeHasContent.current = false;
      setTree([]);
      setSections([]);
      setActiveSectionState(null);
      setLoading(true);
      initialNavDone.current = false;
      bootstrapPageData.current = null;

      // ── Try the single bootstrap endpoint first ──
      // useDocsBootstrap already fired the request and hydrated AuthContext.
      // If its data is available, consume it directly — no sequential fetches needed.
      if (docsBootstrapData && !docsBootstrapError) {
        const secs: Section[] = docsBootstrapData.sections || [];
        setSections(secs);

        // Determine active section from URL or fall back to first
        const sSlug = sectionSlug;
        const selected = sSlug ? secs.find(s => s.slug === sSlug) : null;
        if (selected) {
          setActiveSectionState(selected);
        } else if (secs[0]) {
          setActiveSectionState(secs[0]);
        }

        // Set tree from bootstrap response
        const allRoots = docsBootstrapData.tree || [];
        fullReadOnlyTree.current = allRoots;

        // Filter to active section's children
        const activeSSlug = sSlug || secs[0]?.slug;
        let sectionRoot = activeSSlug ? allRoots.find((n: any) => n.slug === activeSSlug) : null;
        if (!sectionRoot && allRoots.length > 0) sectionRoot = allRoots[0];
        const filtered = sectionRoot?.children || [];
        setTree(filtered);
        treeHasContent.current = filtered.length > 0;
        const isPres0 = sectionRoot?.sectionType === "presentations";
        if (sectionRoot && (isPres0 || hasDeck(filtered))) {
          const slug0 = sectionRoot.slug;
          void withCardBodies(projectSlug, slug0, filtered, isPres0).then((enriched) => {
            if (activeSectionSlugRef.current && activeSectionSlugRef.current !== slug0) return;
            setTree(enriched);
            const idx = fullReadOnlyTree.current.findIndex((n: any) => n.slug === slug0);
            if (idx >= 0) fullReadOnlyTree.current[idx] = { ...fullReadOnlyTree.current[idx], children: enriched };
          });
        }

        // Store bootstrap page body so ContentPageView can skip its own fetch
        if (docsBootstrapData.page != null) {
          bootstrapPageData.current = docsBootstrapData.page;
        }

        // Create a synthetic "main" branch so the rest of the code doesn't break
        const mainBranch: Branch = { id: "main", name: "main", slug: "main", source: "local", createdAt: "", updatedAt: "" };
        setBranches([mainBranch]);
        setActiveBranchState(mainBranch);
        setError(null);
        setLoading(false);
        bootstrapDone.current = true;
        return;
      }

      // ── Wait for bootstrap hook to finish loading before falling back ──
      if (docsBootstrapLoading) {
        // Still loading — don't fall back yet, wait for next render
        return;
      }

      // ── Fallback: bootstrap failed or returned error — use sequential fetches ──
      let cancelled = false;
      (async () => {
        try {
          // Fetch sections from docs API
          const secRes = await authFetch(`/api/docs/${projectSlug}/sections`);
          if (cancelled) return;
          if (secRes.ok) {
            const secs: Section[] = await secRes.json();
            if (cancelled) return;
            setSections(secs);
            const sSlug = sectionSlug;
            const selected = sSlug ? secs.find(s => s.slug === sSlug) : null;
            if (selected) {
              setActiveSectionState(selected);
            } else if (secs[0]) {
              setActiveSectionState(secs[0]);
            }
          }
          // Fetch tree from docs API
          const treeRes = await authFetch(`/api/docs/${projectSlug}/tree`);
          if (cancelled) return;
          if (treeRes.ok) {
            const treeData = await treeRes.json();
            if (cancelled) return;
            const allRoots = treeData.tree || treeData;
            // Store full tree for section switching
            fullReadOnlyTree.current = allRoots;
            // Filter to active section's children
            const sSlug2 = sectionSlug;
            let sectionRoot = sSlug2 ? allRoots.find((n: any) => n.slug === sSlug2) : null;
            // Fallback: if URL section not found, use first section
            if (!sectionRoot && allRoots.length > 0) {
              sectionRoot = allRoots[0];
            }
            const filtered = sectionRoot?.children || [];
            // For presentation sections, fetch card bodies and inject into tree nodes
            const isPres2 = sectionRoot?.sectionType === "presentations";
            if ((isPres2 || hasDeck(filtered)) && filtered.length > 0) {
              const enriched = await withCardBodies(projectSlug, sectionRoot.slug, filtered, isPres2);
              setTree(enriched);
              treeHasContent.current = enriched.length > 0;
              // Also update the full tree cache
              const idx = allRoots.findIndex((n: any) => n.slug === sSlug2);
              if (idx >= 0) {
                allRoots[idx] = { ...allRoots[idx], children: enriched };
                fullReadOnlyTree.current = allRoots;
              }
            } else {
              setTree(filtered);
              treeHasContent.current = filtered.length > 0;
            }
          }
          // Create a synthetic "main" branch so the rest of the code doesn't break
          const mainBranch: Branch = { id: "main", name: "main", slug: "main", source: "local", createdAt: "", updatedAt: "" };
          setBranches([mainBranch]);
          setActiveBranchState(mainBranch);
          setError(null);
          setLoading(false);
          bootstrapDone.current = true;
        } catch (err) {
          if (!cancelled) {
            setError(String(err));
            setLoading(false);
            bootstrapDone.current = true;
          }
        }
      })();
      return () => { cancelled = true; };
    }

    // In local preview mode, skip bootstrap and use individual fetches
    if (isLocalPreview) {
      bootstrapDone.current = true;
      fetchBranches();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const sSlug = sectionSlug || "";
        const bSlug = branchSlugRef.current || "main";
        const sectionParam = sSlug ? `&section_slug=${encodeURIComponent(sSlug)}` : "";
        const res = await authFetch(
              `/api/projects/${projectSlug}/studio/bootstrap?branch_slug=${encodeURIComponent(bSlug)}${sectionParam}`
            );
        if (!res.ok || cancelled) {
          if (!cancelled) {
            bootstrapDone.current = true;
            fetchBranches();
          }
          return;
        }
        const data = await res.json();
        if (cancelled) return;

        // Hydrate user profile from bootstrap to avoid separate /api/me fetch
        if (data.user) {
          hydrateUser(data.user);
        }

        const branchesData: Branch[] = data.branches || [];
        if (branchesData.length === 0) {
          bootstrapDone.current = true;
          fetchBranches();
          return;
        }
        setBranches(branchesData);
        const currentSlug = branchSlugRef.current;
        const fromUrl = currentSlug
          ? branchesData.find((b: Branch) => b.slug === currentSlug) || branchesData.find((b: Branch) => b.name === currentSlug)
          : null;
        const selected = fromUrl || branchesData.find((b: Branch) => b.name === "main") || branchesData[0] || null;
        setActiveBranchState(selected);
        if (selected) { try { sessionStorage.setItem("cortex_active_branch", selected.slug); } catch { /* */ } }

        // Set sections
        const sectionsData: Section[] = data.sections || [];
        setSections(sectionsData);
        const nonSync = sectionsData;
        const fromUrlSection = nonSync.find((s: Section) => s.slug === sSlug);
        const firstContent = nonSync[0] || null;
        const selectedSection = fromUrlSection || firstContent;
        if (selectedSection) setActiveSectionState(selectedSection);

        // Set tree — unwrap section/root wrappers so only content nodes remain.
        // Bootstrap may return [rootNode, sectionNode] where children are nested inside.
        const rawTree: ContentNode[] = data.tree || [];
        let filteredTree = rawTree.filter((n: ContentNode) => (n as any).nodeType !== "root" && n.slug !== "__root__");
        // If the remaining top-level nodes are sections, unwrap to their children
        if (filteredTree.length > 0 && filteredTree.every((n: ContentNode) => n.nodeType === "section")) {
          filteredTree = filteredTree.flatMap((n: ContentNode) => n.children || []);
        }
        setTree(filteredTree);
        treeHasContent.current = filteredTree.length > 0;
        setTreeBranchId(data.activeBranchId || selected?.id || null);
        setStateHashes(data.stateHashes || {});
        setError(null);
        setLoading(false);

        // Record which branch bootstrap loaded so downstream effects can skip
        // redundant fetches for the same branch.
        bootstrapBranchId.current = data.activeBranchId || selected?.id || null;
        bootstrapSectionsBranchId.current = data.activeBranchId || selected?.id || null;
        bootstrapDone.current = true;
      } catch (err) {
        if (!cancelled) {
          bootstrapDone.current = true;
          fetchBranches();
        }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSlug, isLocalPreview, docsBootstrapData, docsBootstrapLoading, docsBootstrapError]);

  // In hosted (non-preview) mode, re-fetch branches when URL ?branch= param changes
  // so the correct branch is selected. In preview mode this is handled by SSE events.
  useEffect(() => {
    if (readOnly) return;
    if (!isLocalPreview && urlBranch && bootstrapDone.current) fetchBranches();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlBranch, isLocalPreview]);
  // Debounce tree fetches: during a branch switch, multiple deps change in rapid
  // succession (activeBranch?.id, activeSection?.slug, reloadTick). Without debounce
  // each change triggers a separate fetchTree call → visible double reload.
  const treeFetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (readOnly) return; // Published docs already loaded tree in bootstrap
    if (!activeBranch) return;
    if (!activeSection) { setLoading(false); return; }
    // Skip until bootstrap is done — bootstrap already loaded the initial tree
    if (!bootstrapDone.current) return;
    // Skip if this is the same branch that bootstrap already loaded
    if (bootstrapBranchId.current && bootstrapBranchId.current === activeBranch.id) {
      bootstrapBranchId.current = null; // Clear so subsequent changes fetch normally
      return;
    }
    bootstrapBranchId.current = null; // Clear for safety
    // During a transactional branch switch the SSE handler orchestrates
    // fetchTree itself — skip the debounced effect to avoid double fetches.
    if (branchSwitchingRef.current) return;
    const bid = activeBranch.id;
    if (treeFetchTimer.current) clearTimeout(treeFetchTimer.current);
    treeFetchTimer.current = setTimeout(() => {
      treeFetchTimer.current = null;
      fetchTree(bid);
    }, 50);
    return () => { if (treeFetchTimer.current) { clearTimeout(treeFetchTimer.current); treeFetchTimer.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch?.id, activeBranch?.source, activeSection?.slug, reloadTick]);

  // After tree loads (section switch OR initial load), auto-navigate to first
  // visible leaf when the URL has no sub-path (i.e. we're at the section root).
  const initialNavDone = useRef(false);
  useEffect(() => {
    if (isPrintRoute) return;
    if (loading || tree.length === 0) return;

    const sSlug = activeSection?.slug;
    if (!sSlug) return;
    const basePath = `${appBase}/projects/${projectSlug}/${sSlug}`;
    const navParams = new URLSearchParams();
    if (!readOnly) {
      navParams.set("branch", activeBranch?.slug || "main");
      if (activeBranch?.source) {
        navParams.set("source", activeBranch.source);
      } else if (isLocalPreview) {
        navParams.set("source", "local");
      }
    }
    const branchParam = navParams.toString() ? `?${navParams.toString()}` : "";
    const subPath = location.pathname.startsWith(basePath)
      ? location.pathname.slice(basePath.length).replace(/^\//, "")
      : "";

    // If there's already a sub-path in the URL, skip auto-navigation
    if (subPath) {
      initialNavDone.current = true;
      pendingSectionSwitch.current = false;
      return;
    }

    // Fire on explicit section switch, initial load (once), or when at section root
    const isInitial = !initialNavDone.current;
    const isSectionSwitch = pendingSectionSwitch.current;
    const isAtSectionRoot = !subPath;
    if (!isInitial && !isSectionSwitch && !isAtSectionRoot) return;

    pendingSectionSwitch.current = false;
    initialNavDone.current = true;

    // Navigate to first leaf with content
    const firstLeaf = findFirstLeaf(tree);
    if (firstLeaf) {
      navigate(`${basePath}/${firstLeaf}${branchParam}`, { replace: true });
    }
  }, [loading, tree, activeSection?.slug, activeSection?.sectionType, projectSlug, activeBranch?.slug, location.pathname, navigate, appBase, readOnly]);

  // ── Sections ──
  const fetchSections = useCallback(async (branchIdOverride?: string, sourceOverride?: string) => {
    try {
      const bid = branchIdOverride || activeBranch?.id;
      const params = new URLSearchParams();
      if (bid) params.set("branch_id", bid);
      params.set("source", sourceOverride || getSourceParam());
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await authFetch(`/api/projects/${projectSlug}/studio/sections${qs}`);
      if (!res.ok) return;
      const data: Section[] = await res.json();
      setSections(data);
      // Resolve active section from URL sectionSlug param.
      //
      // Branch switch semantics: the old branch may have had a section
      // slug the new branch lacks (e.g. the section was renamed or
      // removed, or only exists on another branch). In that case the
      // URL path still points at the old section and the renderer
      // would try to look up content that doesn't exist here. Detect
      // that and navigate pathname to the first section's home so the
      // UI lands on a valid page. Keeping the old stale activeSection
      // or dropping into a "Page not found" state would be worse UX.
      const urlSlug = sectionSlug;
      const fromUrl = urlSlug ? data.find((s) => s.slug === urlSlug) : null;
      const firstContent = data[0] || null;

      if (urlSlug && !fromUrl && firstContent && !readOnly) {
        // Current URL section doesn't exist on this branch — rewrite
        // the pathname to the first section's home. Keep the branch
        // and source query params in place.
        const p = location.pathname;
        const shareMatch = p.match(/^\/share\/[^/]+/);
        const localAppBase = shareMatch ? shareMatch[0] : (p.startsWith("/docs") ? "/docs" : "/studio");
        const homeSlug = firstContent.homePage ? `/${firstContent.homePage}` : "";
        const target = `${localAppBase}/projects/${projectSlug}/${firstContent.slug}${homeSlug}${location.search}`;
        navigate(target, { replace: true });
        setActiveSectionState(firstContent);
        activeSectionSlugRef.current = firstContent.slug;
        return;
      }

      const selected = fromUrl || activeSection || firstContent;
      if (selected && selected.id !== activeSection?.id) {
        setActiveSectionState(selected);
        activeSectionSlugRef.current = selected.slug; // Update ref immediately for fetchTree
      }
    } catch {
      // Sections fetch is non-critical
    }
  }, [projectSlug, activeBranch?.id, sectionSlug, getSourceParam, navigate, location.pathname, location.search, readOnly]);

  // Track the branch ID that bootstrap loaded sections for — skip redundant fetch
  const bootstrapSectionsBranchId = useRef<string | null>(null);
  useEffect(() => { if (readOnly) return; if (activeBranch && !branchSwitchingRef.current && bootstrapDone.current) { if (bootstrapSectionsBranchId.current && bootstrapSectionsBranchId.current === activeBranch.id) { bootstrapSectionsBranchId.current = null; return; } bootstrapSectionsBranchId.current = null; fetchSections(); } }, [activeBranch?.id, activeBranch?.source]);

  // Listen for preview-reload events from CLI commands (commit, pull, switch, reset).
  // Differentiate event types to avoid redundant tree reloads:
  //   branch-switch  → fetchBranches + fetchSections only (UI already switched)
  //   stage/unstage  → bump mutation only (staging area changed, not tree content)
  //   content-change → skip if it arrives shortly after a branch-switch (watcher noise from ensureBranchOnDisk)
  //   commit/reset/restore/reload → bump reloadTick to refresh tree
  const lastBranchSwitchSSE = useRef(0);
  useEffect(() => {
    const handler = async (e: Event) => {
      const eventType = (e as CustomEvent)?.detail?.type || "reload";

      if (eventType === "branch-switch") {
        lastBranchSwitchSSE.current = Date.now();
        // ── Transactional branch switch ──
        // Set the flag BEFORE any fetches so downstream consumers (staging,
        // content resolution) freeze on the old state until we're done.
        branchSwitchingRef.current = true;
        setBranchSwitching(true);
        try {
          // 1. Get the new branch name + source from the server (source of truth)
          let switchSource: string | undefined;
          try {
            const cfgRes = await fetch("/api/preview-config");
            if (cfgRes.ok) {
              const cfg = await cfgRes.json();
              const newName = cfg.activeBranch as string | null;
              if (newName) branchSlugRef.current = newName;
              // Capture the source so fetchTree uses the correct value
              // (React state from fetchBranches hasn't flushed yet).
              if (cfg.activeSource) switchSource = cfg.activeSource as string;
            }
          } catch {}
          // 2. Fetch branches (sets activeBranch state)
          await fetchBranches();
          // 3. Fetch sections for the new branch
          await fetchSections(branchSlugRef.current, switchSource);
          // 4. Fetch tree — pass branch name AND source explicitly because
          //    React state from fetchBranches hasn't flushed yet.
          await fetchTree(branchSlugRef.current, switchSource);
          bumpMutation();
        } finally {
          branchSwitchingRef.current = false;
          setBranchSwitching(false);
        }
        return;
      }

      if (eventType === "stage" || eventType === "unstage" || eventType === "draft") {
        // Draft/staging changed — bump mutation so draft panels re-fetch,
        // but don't reload the tree (content didn't change).
        bumpMutation();
        return;
      }

      // Content-changing events: commit, reset, restore, content-change, reload
      await fetchBranches();
      fetchSections();
      setReloadTick(t => t + 1);
    };
    window.addEventListener("preview-reload", handler);
    return () => window.removeEventListener("preview-reload", handler);
  }, [fetchBranches, fetchSections, fetchTree, bumpMutation]);

  // Sync CMS active branch when CLI switches branches externally.
  // previewActiveBranch comes from the preview server's HEAD (updated on SSE reload).
  // When it changes, find the matching CMS branch and switch to it.
  // Sync CMS active branch when the preview server's active branch changes externally.
  // In local preview mode, fetchBranches() (triggered by preview-reload) already selects
  // the correct branch via branchSlug, so we skip setActiveBranchState here to avoid
  // a redundant state update that would cause a double tree reload.
  const prevPreviewBranch = useRef(previewActiveBranch);
  useEffect(() => {
    if (!previewActiveBranch || previewActiveBranch === prevPreviewBranch.current) return;
    prevPreviewBranch.current = previewActiveBranch;
    // In local preview, fetchBranches already handles branch selection — skip to avoid double reload
    if (isLocalPreview) return;
    // Hosted CMS: manually sync the active branch and update URL params
    const match = branches.find(
      (b) => b.slug === previewActiveBranch || b.name === previewActiveBranch
    );
    if (match && match.id !== activeBranch?.id) {
      setActiveBranchState(match);
      const params = new URLSearchParams(location.search);
      params.set("branch", match.slug);
      params.delete("source");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [previewActiveBranch, branches, activeBranch?.id, navigate, location.pathname, location.search, isLocalPreview]);

  // In preview mode, strip any stale ?branch= / ?source= params from the URL.
  const createSection = useCallback(async (data: { title: string; slug: string; sectionType: string; baseUrl?: string }): Promise<Section> => {
    const branchId = activeBranch?.id;
    const payload: Record<string, any> = { branchId, ...data };
    if (!payload.baseUrl) delete payload.baseUrl; // don't send empty string
    const res = await authFetch(`/api/projects/${projectSlug}/studio/sections`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to create section: ${res.statusText}`);
    }
    const section: Section = await res.json();
    await fetchSections();
    bumpMutation();
    return section;
  }, [projectSlug, activeBranch?.id, fetchSections, bumpMutation]);

  const deleteSection = useCallback(async (sectionId: string): Promise<void> => {
    const branchParam = activeBranch?.id ? `?branch_id=${activeBranch.id}` : "";
    const res = await authFetch(`/api/projects/${projectSlug}/studio/sections/${sectionId}${branchParam}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to delete section: ${res.statusText}`);
    }
    await fetchSections();
    bumpMutation();
  }, [projectSlug, activeBranch?.id, fetchSections, bumpMutation]);

  const renameSection = useCallback(async (sectionId: string, title: string): Promise<void> => {
    const branchParam = activeBranch?.id ? `?branch_id=${encodeURIComponent(activeBranch.id)}` : "";
    const res = await authFetch(`/api/projects/${projectSlug}/studio/sections/${sectionId}${branchParam}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to rename section: ${res.statusText}`);
    }
    await fetchSections();
    bumpMutation();
  }, [projectSlug, activeBranch?.id, fetchSections, bumpMutation]);

  const updateSection = useCallback(async (sectionId: string, updates: { title?: string; baseUrl?: string; visibility?: string; sectionType?: string }): Promise<void> => {
    const branchParam = activeBranch?.id ? `?branch_id=${encodeURIComponent(activeBranch.id)}` : "";
    const res = await authFetch(`/api/projects/${projectSlug}/studio/sections/${sectionId}${branchParam}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to update section: ${res.statusText}`);
    }
    await fetchSections();
    bumpMutation();
  }, [projectSlug, activeBranch?.id, fetchSections, bumpMutation]);

  const reorderSections = useCallback(async (orderedSlugs: string[], movedSlug?: string): Promise<void> => {
    const branchId = activeBranch?.id;
    if (!branchId) return;
    const res = await authFetch(`/api/projects/${projectSlug}/studio/sections/reorder`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ branchId, orderedSlugs, movedSlug }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to reorder sections: ${res.statusText}`);
    }
    await fetchSections();
    bumpMutation();
  }, [projectSlug, activeBranch?.id, fetchSections, bumpMutation]);

  const setActiveSection = useCallback((section: Section) => {
    setActiveSectionState(section);
    // In read-only mode, re-filter tree from cached full tree (no API call needed)
    if (readOnly && fullReadOnlyTree.current.length > 0) {
      const sectionRoot = fullReadOnlyTree.current.find((n: any) => n.slug === section.slug);
      const filtered = sectionRoot?.children || [];
      // For presentation sections, fetch card bodies if not already loaded
      const isPres = sectionRoot?.sectionType === "presentations";
      if ((isPres || hasDeck(filtered)) && filtered.length > 0) {
        const needsFetch = lacksCardBody(filtered);
        if (needsFetch) {
          setTree(filtered); // show structure immediately
          treeHasContent.current = true;
          // Fetch bodies in background
          (async () => {
            const enriched = await withCardBodies(projectSlug, section.slug, filtered, isPres);
            setTree(enriched);
            // Update cache
            const idx = fullReadOnlyTree.current.findIndex((n: any) => n.slug === section.slug);
            if (idx >= 0) {
              fullReadOnlyTree.current[idx] = { ...fullReadOnlyTree.current[idx], children: enriched };
            }
          })();
        } else {
          setTree(filtered);
          treeHasContent.current = filtered.length > 0;
        }
      } else {
        setTree(filtered);
        treeHasContent.current = filtered.length > 0;
      }
    } else {
      // Only clear tree if switching to a different section
      if (section.slug !== activeSectionSlugRef.current) {
        setTree([]);
        treeHasContent.current = false;
        setLoading(true);
        activeSectionSlugRef.current = section.slug;
      }
    }
    pendingSectionSwitch.current = true;
    // Include branch query params only in non-preview (hosted CMS) mode
    const params = new URLSearchParams();
    if (!readOnly) {
      params.set("branch", activeBranch?.slug || "main");
      if (activeBranch?.source) params.set("source", activeBranch.source);
    }
    const qs = params.toString() ? `?${params.toString()}` : "";
    if (section.homePage && (section.sectionType === "content" || section.sectionType === "release-notes")) {
      navigate(`${appBase}/projects/${projectSlug}/${section.slug}/${section.homePage}${qs}`, { replace: true });
    } else {
      navigate(`${appBase}/projects/${projectSlug}/${section.slug}${qs}`, { replace: true });
    }
  }, [navigate, projectSlug, activeBranch?.slug, activeBranch?.source, appBase, readOnly]);

  const setActiveBranch = useCallback(async (branch: Branch) => {
    if (readOnly) return;
    setActiveBranchState(branch);
    // Persist branch in sessionStorage so it survives settings navigation
    try { sessionStorage.setItem("cortex_active_branch", branch.slug); } catch { /* */ }
    // Update URL to reflect branch
    const params = new URLSearchParams(location.search);
    params.set("branch", branch.slug);
    if (branch.source) {
      params.set("source", branch.source);
    } else if (isLocalPreview) {
      params.set("source", "local");
    } else {
      params.delete("source");
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });

    // Transactional switch: fetch sections for the new branch first so
    // activeSection resolves against the right list, then fetch the tree
    // using that section. Without this, the debounced tree-fetch effect
    // races with fetchSections — it can fire while activeSection still
    // points at the outgoing branch's section, returning a tree that
    // matches neither branch.
    //
    // branchSwitchingRef suppresses the standalone tree-fetch useEffect
    // (which checks this flag and no-ops) so there's no duplicate call.
    branchSwitchingRef.current = true;
    setBranchSwitching(true);
    try {
      await fetchSections(branch.id, branch.source);
      await fetchTree(branch.id, branch.source);
    } finally {
      branchSwitchingRef.current = false;
      setBranchSwitching(false);
    }
  }, [navigate, location.pathname, location.search, readOnly, isLocalPreview, fetchSections, fetchTree]);


  // Global slug-path navigator — handles cross-section links correctly.
  // Extracts the target section from the URL, switches section if needed,
  // then navigates to the full path.
  const navigateToPath = useCallback((targetPath: string) => {
    // Extract section slug from path: /{app}/projects/{project}/{sectionSlug}/...
    const parts = targetPath.split("/");
    const projectsIdx = parts.indexOf("projects");
    const targetSectionSlug = projectsIdx >= 0 && parts.length > projectsIdx + 2 ? parts[projectsIdx + 2] : null;

    // Cross-section: do a full section switch before navigating
    if (targetSectionSlug && activeSection?.slug !== targetSectionSlug) {
      const targetSection = sections.find(s => s.slug === targetSectionSlug);
      if (targetSection) {
        setActiveSectionState(targetSection);
        activeSectionSlugRef.current = targetSection.slug;
        if (readOnly && fullReadOnlyTree.current.length > 0) {
          // Docs app: re-filter tree from cached full tree
          const sectionRoot = fullReadOnlyTree.current.find((n: any) => n.slug === targetSection.slug);
          setTree(sectionRoot?.children || []);
          treeHasContent.current = (sectionRoot?.children?.length || 0) > 0;
        } else {
          // Studio: clear tree so the fetch effect picks up the new section
          setTree([]);
          treeHasContent.current = false;
          setLoading(true);
        }
        pendingSectionSwitch.current = true;
      }
    }

    // Navigate with preserved search params
    const search = window.location.search;
    const hashIdx = targetPath.indexOf("#");
    if (hashIdx >= 0) {
      navigate(targetPath.slice(0, hashIdx) + search + targetPath.slice(hashIdx));
    } else {
      navigate(targetPath + search);
    }
  }, [activeSection?.slug, sections, readOnly, navigate]);

  const createBranch = useCallback(async (name: string, sourceBranchId: string, fromCommitId?: string, sourceOrigin?: "local" | "remote"): Promise<Branch> => {
    const payload: Record<string, string> = { name: name.trim().toLowerCase(), sourceBranchId };
    if (fromCommitId) payload.fromCommitId = fromCommitId;
    if (sourceOrigin) payload.sourceOrigin = sourceOrigin;
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to create branch: ${res.statusText}`);
    }
    const created: Branch = await res.json();
    await fetchBranches();
    return created;
  }, [fetchBranches]);

  const deleteBranch = useCallback(async (id: string): Promise<void> => {
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${id}`, { method: "DELETE" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to delete branch: ${res.statusText}`);
    }
    await fetchBranches();
  }, [fetchBranches]);

  const forcePromoteBranch = useCallback(async (id: string): Promise<Branch> => {
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${id}/force-promote`, { method: "POST" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(
        typeof err.detail === "string" ? err.detail : err.detail?.message || `Failed to overwrite live: ${res.statusText}`
      );
    }
    const promoted: Branch = await res.json();
    await fetchBranches();
    return promoted;
  }, [fetchBranches]);

  const mergePreview = useCallback(async (id: string, targetId?: string): Promise<MergePreviewResult> => {
    const params = targetId ? `?targetBranchId=${targetId}` : "";
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${id}/merge-preview${params}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(typeof err.detail === "string" ? err.detail : err.detail?.message || `Merge preview failed: ${res.statusText}`);
    }
    return res.json();
  }, [projectSlug]);

  const branchDiff = useCallback(async (id: string, targetId?: string): Promise<BranchDiffResult> => {
    const params = targetId ? `?target=${targetId}` : "";
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${id}/diff${params}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(typeof err.detail === "string" ? err.detail : err.detail?.message || `Diff failed: ${res.statusText}`);
    }
    return res.json();
  }, [projectSlug]);

  const checkoutBranch = useCallback(async (branchId: string, branchName: string, branchSlug: string): Promise<Branch> => {
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ branchId, branchName, branchSlug }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to checkout branch: ${res.statusText}`);
    }
    const checked: Branch = await res.json();
    await fetchBranches();
    return checked;
  }, [fetchBranches, projectSlug]);

  const handleConflict = useCallback(async (res: Response) => {
    const data = await res.json();
    if (data.error === "conflict") {
      setConflict(data.message || "The ordering was changed by another contributor. The tree has been refreshed.");
      setStateHashes((prev) => ({ ...prev, ...{ [data.currentHash]: data.currentHash } }));
      await fetchTree();
    }
  }, [fetchTree]);

  // qualifyId prepends the active section's slug to a section-stripped node id
  // so the backend gets the full slug_path and resolveNodeID can't collide
  // with a section of the same name. Leaves null / already-qualified ids alone.
  const sectionSlugForQualify = activeSection?.slug;
  const qualifyId = useCallback((id: string | null | undefined): string | null => {
    if (!id) return null;
    if (!sectionSlugForQualify) return id;
    if (id === sectionSlugForQualify) return id;
    if (id.startsWith(sectionSlugForQualify + "/")) return id;
    return `${sectionSlugForQualify}/${id}`;
  }, [sectionSlugForQualify]);

  const createNode = useCallback(async (data: Partial<ContentNode> & { previousHash?: string | null }): Promise<ContentNode> => {
    // Guard: peek mode without edit mode = read-only
    const isPeeking = previewCurrentBranch && previewCurrentBranch !== previewActiveBranch;
    if (isPeeking && !editMode) throw new Error("Cannot modify content while in peek mode");

    const branchId = data.branchId || activeBranch?.id;
    const sectionId = activeSection?.id;
    // The tree endpoint strips the section prefix from node ids for URL
    // readability (so a page at disk "docs/platform/guides" shows up as
    // "platform/guides" in the sidebar). When we send that id back to the
    // backend, its resolveNodeID does a direct lookup first — which
    // collides with a section if a nested page's leaf slug equals the
    // section's slug (e.g. section "test" containing page "test" → both
    // look like "test"). qualifyId prepends the active section so the
    // backend receives the unambiguous full slug_path.
    const sourceParam = `?source=${getSourceParam()}&branch=${activeBranch?.id || "main"}`;
    const res = await authFetch(`/api/projects/${projectSlug}/studio/nodes${sourceParam}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...data, parentId: qualifyId(data.parentId ?? null), branchId, sectionId }),
    });
    if (res.status === 409) {
      const body = await res.json().catch(() => ({}));
      const detail = body?.detail;
      if (typeof detail === "object" && detail?.error === "conflict") {
        setConflict(detail.message || "The ordering was changed. The tree has been refreshed.");
        setStateHashes((prev) => ({ ...prev, ...(detail.currentHash ? { [detail.currentHash]: detail.currentHash } : {}) }));
        await fetchTree();
        throw new Error("Conflict — tree has been refreshed");
      }
      // Slug duplicate or other 409
      throw new Error(typeof detail === "string" ? detail : "A page with this name already exists here. Please choose a different title.");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to create node: ${res.statusText}`);
    }
    const result = await res.json();
    await fetchTree();
    bumpMutation();
    return result.node;
  }, [activeBranch?.id, fetchTree, handleConflict, bumpMutation, previewCurrentBranch, previewActiveBranch, editMode]);

  const updateNode = useCallback(async (id: string, updates: Partial<ContentNode> & { previousHash?: string; baseContentHash?: string }): Promise<ContentNode> => {
    // Route through object store when editing a non-active branch
    if (editMode && editBranch && editBranch !== previewActiveBranch) {
      const result = await objectStoreWrite(editBranch, updates.slug || '', {
        title: updates.title,
        body: updates.body ?? undefined,
        metadata: undefined,
      });
      await fetchTree();
      bumpMutation();
      return result.node as unknown as ContentNode;
    }

    const fullId = qualifyId(id) ?? id;
    const res = await authFetch(`/api/projects/${projectSlug}/studio/nodes/${fullId}?source=${getSourceParam()}&branch=${activeBranch?.id || "main"}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    if (res.status === 409) {
      const body = await res.json().catch(() => ({}));
      const detail = body?.detail;
      if (typeof detail === "object" && detail?.error === "content_conflict") {
        // The page changed since the editor loaded it (updates.baseContentHash):
        // nothing was saved. Keep the editor open with the writer's text.
        throw new Error(detail.message || "This page was changed after you opened it. Reload it and apply your edit again.");
      }
      if (typeof detail === "object" && detail?.error === "conflict") {
        setConflict(detail.message || "The ordering was changed. The tree has been refreshed.");
        setStateHashes((prev) => ({ ...prev, ...(detail.currentHash ? { [detail.currentHash]: detail.currentHash } : {}) }));
        await fetchTree();
        throw new Error("Conflict — tree has been refreshed");
      }
      throw new Error(typeof detail === "string" ? detail : "A page with this title already exists here. Please choose a different title.");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to update node: ${res.statusText}`);
    }
    const result = await res.json();
    await fetchTree();
    bumpMutation();
    // Backend may return {node: ...} (create) or the node directly (update)
    return result.node || result;
  }, [activeBranch?.id, fetchTree, handleConflict, bumpMutation, editMode, editBranch, previewActiveBranch, objectStoreWrite, projectSlug, getSourceParam, qualifyId]);

  const deleteNode = useCallback(async (id: string): Promise<void> => {
    // Guard: peek mode without edit mode = read-only
    const isPeeking = previewCurrentBranch && previewCurrentBranch !== previewActiveBranch;
    if (isPeeking && !editMode) throw new Error("Cannot modify content while in peek mode");

    const fullId = qualifyId(id) ?? id;
    const res = await authFetch(`/api/projects/${projectSlug}/studio/nodes/${fullId}?source=${getSourceParam()}&branch=${activeBranch?.id || "main"}`, { method: "DELETE" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to delete node: ${res.statusText}`);
    }
    await fetchTree();
    bumpMutation();
  }, [activeBranch?.id, fetchTree, bumpMutation, projectSlug, getSourceParam, previewCurrentBranch, previewActiveBranch, editMode, qualifyId]);

  const reorderNodes = useCallback(async (parentId: string | null, order: string[], previousHash: string): Promise<void> => {
    // Guard: peek mode without edit mode = read-only
    const isPeeking = previewCurrentBranch && previewCurrentBranch !== previewActiveBranch;
    if (isPeeking && !editMode) throw new Error("Cannot modify content while in peek mode");

    const branchId = activeBranch?.id;
    const res = await authFetch(`/api/projects/${projectSlug}/studio/nodes/reorder?source=${getSourceParam()}&branch=${activeBranch?.id || "main"}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ branchId, parentId: qualifyId(parentId), order, previousHash }),
    });
    if (res.status === 409) {
      await handleConflict(res);
      throw new Error("Conflict — tree has been refreshed");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to reorder: ${res.statusText}`);
    }
    await fetchTree();
  }, [activeBranch?.id, fetchTree, handleConflict, previewCurrentBranch, previewActiveBranch, editMode, qualifyId]);

  const moveNode = useCallback(async (nodeId: string, newParentId: string | null, position?: number): Promise<ContentNode> => {
    // Guard: peek mode without edit mode = read-only
    const isPeeking = previewCurrentBranch && previewCurrentBranch !== previewActiveBranch;
    if (isPeeking && !editMode) throw new Error("Cannot modify content while in peek mode");

    const branchId = activeBranch?.id;
    const parentKey = (() => {
      // Find the node's current parent to get the right hash
      const findParent = (nodes: ContentNode[]): string | null | undefined => {
        for (const n of nodes) {
          if (n.id === nodeId) return n.parentId;
          if (n.children) {
            const found = findParent(n.children);
            if (found !== undefined) return found;
          }
        }
        return undefined;
      };
      return findParent(tree) ?? null;
    })();
    const previousHash = stateHashes[parentKey ?? "root"] ?? "";

    const res = await authFetch(`/api/projects/${projectSlug}/studio/nodes/move?source=${getSourceParam()}&branch=${activeBranch?.id || "main"}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        nodeId: qualifyId(nodeId),
        newParentId: qualifyId(newParentId),
        position, previousHash, branchId,
      }),
    });
    if (res.status === 409) {
      const data = await res.clone().json().catch(() => ({}));
      if (typeof data?.detail === "object" && data.detail?.error === "conflict") {
        await handleConflict(res);
        throw new Error("Conflict — tree has been refreshed");
      }
      // Slug conflict or other 409
      throw new Error(typeof data?.detail === "string" ? data.detail : "Move conflict");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to move node: ${res.statusText}`);
    }
    const result = await res.json();
    // Update stateHashes immediately from the response to prevent race conditions
    // when multiple moves happen in quick succession (e.g. drag-and-drop)
    if (result.stateHashes) {
      setStateHashes(result.stateHashes);
    }
    await fetchTree();
    return result.node;
  }, [activeBranch?.id, tree, stateHashes, fetchTree, handleConflict, previewCurrentBranch, previewActiveBranch, editMode, qualifyId]);

  const getNodeBySlug = useCallback(async (slugPath: string): Promise<ContentNode> => {
    // Try to resolve from in-memory tree first (avoids API round-trip)
    const effectiveTree = peekMode ? peekTree : tree;
    if (effectiveTree.length > 0) {
      const segments = slugPath.split("/").filter(Boolean);
      let nodes = effectiveTree;
      let found: ContentNode | null = null;
      for (const seg of segments) {
        found = nodes.find((n) => n.slug === seg) || null;
        if (!found) break;
        nodes = found.children || [];
      }
      if (found) {
        // In read-only mode, tree nodes never have bodies (stripped from build snapshot).
        // Return the node as-is — ContentPageView handles lazy body fetch via /api/docs/.
        if (readOnly) {
          return found;
        }
        // Node types that should have bodies — if body is missing, fall through
        // to the API fetch so the full node (with body) is returned.
        // Categories with hasBody also need their body fetched from the API.
        const needsBody = found.nodeType === "page" || found.nodeType === "api_endpoint" || found.nodeType === "card";
        const categoryNeedsBody = found.nodeType === "category" && found.hasBody && !found.body;
        if ((!needsBody && !categoryNeedsBody) || found.body != null) {
          return found;
        }
        // body is missing for a node type that should have one — fall through to API
      }
    }

    // Fallback to API call if not found in tree
    const isPeeking = previewCurrentBranch && previewCurrentBranch !== previewActiveBranch;
    const bid = isPeeking ? previewCurrentBranch : activeBranch?.id;
    const params = new URLSearchParams();
    if (bid) params.set("branch", bid);
    const sSlug = activeSectionSlugRef.current || activeSection?.slug || sectionSlug;
    if (sSlug) params.set("section_slug", sSlug);
    params.set("source", getSourceParam());
    const qs = params.toString();
    const url = `/api/projects/${projectSlug}/studio/nodes/by-slug/${slugPath}${qs ? `?${qs}` : ""}`;
    const res = await authFetch(url);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to resolve slug: ${res.statusText}`);
    }
    return res.json();
  }, [activeBranch?.id, activeSection?.slug, sectionSlug, previewCurrentBranch, previewActiveBranch, tree, peekMode, peekTree]);

  const searchDocs = useCallback(async (query: string): Promise<DocSearchResult[]> => {
    const bid = activeBranch?.id || "";
    const params = new URLSearchParams({ q: query });
    if (bid) params.set("branch", bid);
    const res = await authFetch(`/api/projects/${projectSlug}/studio/doc-search?${params.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Search failed: ${res.statusText}`);
    }
    return res.json();
  }, [projectSlug, activeBranch?.id]);

  const clearConflict = useCallback(() => setConflict(null), []);

  return (
    <CmsContext.Provider value={{
      branches, activeBranch, setActiveBranch, fetchBranches,
      createBranch, deleteBranch, forcePromoteBranch, mergePreview, branchDiff, checkoutBranch,
      sections, activeSection, setActiveSection, fetchSections, createSection, deleteSection, renameSection, updateSection, reorderSections,
      tree, stateHashes, loading, error, conflict, clearConflict, refetchTree: fetchTree, treeBranchId,
      createNode, updateNode, deleteNode, reorderNodes, moveNode, getNodeBySlug, searchDocs,
      navigateToPath,
      mutationVersion, bumpMutation, branchSwitching, suppressBranchSwitchSSE,
      peekMode, peekCommitId, peekCommitMessage, peekCommitSequence, peekTree, peekLoading, peekSections, setPeekMode, setPeekCommitId,
      bootstrapPageData,
    }}>
      {children}
    </CmsContext.Provider>
  );
}

export function useCms(): CmsContextValue {
  const ctx = useContext(CmsContext);
  if (!ctx) throw new Error("useCms must be used within <CmsProvider>");
  return ctx;
}

// Use this when a component may render both inside AND outside the CMS
// context — e.g. MarkdownRenderer, which is reused for diff/review panels
// that sit outside any doc-viewing tree.
export function useOptionalCms(): CmsContextValue | null {
  return useContext(CmsContext) ?? null;
}
